import { AxiosRequestConfig } from "axios";
import ApiClient from "src/utils/ApiClient";
import isMockView from "src/utils/Api/isMockView";
import {
  DeductionConsentEmployee,
  DeductionConsentFile,
  DeductionConsentDocumentsResponse,
  DeductionConsentsPageResponse,
  DeductionConsentsQueryParams,
} from "./models";

type RequestConfig = Pick<AxiosRequestConfig, "signal">;

const DeductionConsentsApiClient = new ApiClient({
  baseURL: "/api/salary-deduction/api/rest",
  headers: {},
}).client;

const normalizeDocument = (raw: unknown): DeductionConsentFile | null => {
  const item = (raw || {}) as {
    id?: string;
    documentId?: string;
    fileId?: string;
    createdAt?: string;
    fileSize?: number;
  };

  const id = item.id || item.documentId || item.fileId;
  if (!id) {
    return null;
  }

  return {
    id,
    createdAt: item.createdAt || "",
    fileSize: item.fileSize,
  };
};

export const fetchDeductionConsents = async (
  params: DeductionConsentsQueryParams,
  { signal }: RequestConfig
): Promise<DeductionConsentsPageResponse> => {
  if (isMockView()) {
    const response = await fetch(
      "/mockData/ahr/deduction-consents/list.json"
    );
    const mockData = await response.json();
    
    await new Promise((resolve) => setTimeout(resolve, 500));
    
    let filteredContent = [...(mockData?.content || [])];
    
    if (params.firstName) {
      const searchTerm = params.firstName.toLowerCase();
      filteredContent = filteredContent.filter((employee) =>
        employee.firstName.toLowerCase().includes(searchTerm)
      );
    }
    
    if (params.lastName) {
      const searchTerm = params.lastName.toLowerCase();
      filteredContent = filteredContent.filter((employee) =>
        employee.lastName.toLowerCase().includes(searchTerm)
      );
    }
    
    if (params.fk) {
      const searchTerm = params.fk.toLowerCase();
      filteredContent = filteredContent.filter((employee) =>
        employee.fk.toLowerCase().includes(searchTerm)
      );
    }
    
    const start = params.page * params.size;
    const end = start + params.size;
    const paginatedContent = filteredContent.slice(start, end);
    
    return {
      page: {
        content: paginatedContent,
        totalElements: filteredContent.length,
        totalPages: Math.ceil(filteredContent.length / params.size),
        size: params.size,
        page: params.page,
      },
    };
  }

  const { data } = await DeductionConsentsApiClient.get(`/v1/employees`, {
    params,
    paramsSerializer: {
      indexes: null,
    },
    signal,
  });
  const employees = (data?.content || []) as DeductionConsentEmployee[];

  return {
    page: {
      content: employees,
      totalElements: data?.totalElements ?? employees.length,
      totalPages: data?.totalPages ?? Math.max(1, Math.ceil(employees.length / params.size)),
      size: data?.size ?? params.size,
      page: data?.page ?? params.page,
    },
  };
};

export const fetchDeductionConsentDocuments = async (
  employeeId: string,
  { signal }: RequestConfig
): Promise<DeductionConsentFile[]> => {
  if (isMockView()) {
    const response = await fetch("/mockData/ahr/deduction-consents/list.json");
    const mockData = await response.json();
    return mockData?.documentsByEmployeeId?.[employeeId] || [];
  }

  const { data } = await DeductionConsentsApiClient.get<
    DeductionConsentDocumentsResponse | DeductionConsentFile[]
  >(`/v1/documents/${employeeId}`, {
    signal,
  });

  if (Array.isArray(data)) {
    return data
      .map((item) => normalizeDocument(item))
      .filter((item): item is DeductionConsentFile => item !== null);
  }

  const pageContent = (data as { page?: { content?: unknown[] } })?.page?.content;
  const content = (data as { content?: unknown[] })?.content;
  const rawDocuments = pageContent || content || [];

  return rawDocuments
    .map((item) => normalizeDocument(item))
    .filter((item): item is DeductionConsentFile => item !== null);
};

export const downloadDeductionConsentFile = async (
  fileId: string,
  { signal }: RequestConfig
): Promise<Blob> => {
  if (isMockView()) {
    console.log("Mock mode: Simulating file download for fileId:", fileId);
    await new Promise((resolve) => setTimeout(resolve, 500));
    
    const mockContent = `Mock file content for document ID: ${fileId}`;
    return new Blob([mockContent], { type: "application/pdf" });
  }

  const { data } = await DeductionConsentsApiClient.get(
    `/v1/documents/${fileId}/download`,
    {
      responseType: "blob",
      signal,
    }
  );
  
  return data;
};

